import { useMemo } from 'react';

import { mutate } from 'swr';

import {
  getLocalidades as axiosGetLocalidades,
  getProvincia as axiosGetProvincias,
  getUser as axiosGetUser,
  postSolicitudCambioUser as axiosPostSolicitudCambio,
  postUser as axiosPostUser,
} from '../../axios/User';
import { GET_PROVINCIA, PERSONAL_DATA } from '../../axios/Utils/constants';
import {
  LocalidadRes,
  SolicitudCambioUserReq,
  UserReq,
  UserRes,
} from '../../types/User/types';
import { capitalizeEveryWord } from '../../utils/string';
import { useCreateEntity, useFetchEntity } from '../Utils/useEntity';

const usePostUser = () => {
  const { create, isLoading, error } = useCreateEntity<UserReq, boolean>(axiosPostUser);

  const postUser = (body: UserReq) =>
    create(body, (ok) => {
      if (ok) mutate(PERSONAL_DATA);
    });

  return { postUser, isLoading, error };
};

const useSolicitudCambioUser = () => {
  const { create, isLoading, error } = useCreateEntity<SolicitudCambioUserReq, boolean>(
    axiosPostSolicitudCambio,
  );

  return { postSolicitudCambioUser: create, isLoading, error };
};

const useGetUser = () => {
  const {
    data: user,
    isValidating,
    error,
    mutate,
  } = useFetchEntity<UserRes>(PERSONAL_DATA, axiosGetUser);

  return { user, isValidating, error, mutate };
};

const useGetLocalidades = (provincia?: string) => {
  const { data, isValidating, error, mutate } = useFetchEntity<LocalidadRes[]>(
    provincia ? `localidades-${provincia}` : null,
    () => (provincia ? axiosGetLocalidades(provincia) : Promise.resolve([])),
  );

  // ✅ Usar useMemo para evitar crear un nuevo array en cada render
  const localidadesOptions = useMemo(
    () =>
      data?.map((localidad) => ({
        value: String(localidad.id),
        label: capitalizeEveryWord(localidad.nombre),
      })) ?? [],
    [data], // Solo recalcular cuando data cambia
  );

  return {
    localidades: localidadesOptions,
    isValidating,
    error,
    mutate,
  };
};

const useGetProvincia = (localidadId?: string) => {
  const { data, isValidating, error, mutate } = useFetchEntity<any>(
    localidadId ? [GET_PROVINCIA, localidadId].join(':') : null,
    () =>
      localidadId
        ? axiosGetProvincias(GET_PROVINCIA, localidadId)
        : Promise.resolve(undefined),
  );

  return { provincia: data, isValidating, error, mutate };
};

export {
  usePostUser,
  useSolicitudCambioUser,
  useGetUser,
  useGetLocalidades,
  useGetProvincia,
};
